import React, { useEffect, useRef } from 'react';
import { Info } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import ErrorMessage from '../ui/ErrorMessage';
import Disclaimer from '../ui/Disclaimer';
import { DISCLAIMER_LINK_CLASS_NAME } from '../../utils/disclaimerContent';
import { useHubspot } from '../../hooks/useHubspot';

type SSNVerificationFormProps = {
  formData: FormData;
  loading: boolean;
  error: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function SSNVerificationForm({
  formData,
  loading,
  error,
  onChange,
  onSubmit
}: SSNVerificationFormProps) {
  const { t } = useTranslation('wizard');
  const inputRef = useRef<HTMLInputElement>(null);
  const hubspot = useHubspot();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.ssnLast4 && formData.ssnLast4.length === 4) {
      // Track SSN verification in HubSpot
      hubspot.trackEvent('ssn_verification_submitted', {
        email: formData.email,
        form_step: 'ssn_verification'
      });

      onSubmit({ ssnLast4: formData.ssnLast4 });
    }
  };

  const isValidLast4SSN = (ssn: string | undefined) => {
    return ssn ? /^\d{4}$/.test(ssn) : false;
  };

  return (
    <form id="ssn-verification-form" name="ssn-verification-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center">
        <Title 
          id="ssn-verification-title"
          name="ssn-verification-title"
          as="h1" 
          className="mb-2"
        >
          {t('ssnVerification.title')}
        </Title>
        <Description 
          id="ssn-verification-description"
          name="ssn-verification-description"
          size="lg"
        >
            <span className="inline-flex items-center justify-center gap-2">
            {t('ssnVerification.description')}
            <span className="ml-1 inline-flex items-center relative group">
              <Info className="h-5 w-5 text-gray-600 cursor-pointer" aria-hidden />
                <span className="pointer-events-none absolute left-1/2 -translate-x-[90%] sm:left-1/2 sm:-translate-x-[60%] top-full mt-2 w-max min-w-[14rem] max-w-[90vw] sm:w-72 p-2 bg-gray-700 text-white text-sm rounded-md text-left whitespace-normal break-normal opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity z-50 shadow-lg">
                {t('ssnVerification.tooltip')}
              </span>
              <span className="sr-only">{t('ssnVerification.tooltipSrLabel')}</span>
            </span>
          </span>
        </Description>
      </div>

      <div className="space-y-4">
        <div>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              name="ssnLast4"
              id="ssn-verification-input"
              value={formData.ssnLast4 || ''}
              onChange={onChange}
              maxLength={4}
              placeholder={t('ssnVerification.placeholder')}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#b3905e] focus:ring-2 focus:ring-[#b3905e]/20 transition duration-200 text-center text-2xl tracking-widest"
              required
              inputMode="numeric"
            />
          </div>
        </div>
      </div>

      <ErrorMessage message={error} />
      
      <Disclaimer
        id="ssn-verification-disclaimer"
        name="ssn-verification-disclaimer"
      >
        <p className="mb-3">{t('ssnVerification.disclaimerIntro')}</p>
        <p className="mb-3">
          <Trans
            i18nKey="ssnVerification.disclaimerConsent"
            ns="wizard"
            components={{
              partnersLink: (
                <a
                  href="https://symplelending.com/partners"
                  className={DISCLAIMER_LINK_CLASS_NAME}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </p>
        <p>
          <Trans
            i18nKey="ssnVerification.disclaimerAck"
            ns="wizard"
            components={{
              partnerLink: (
                <a
                  href="https://symplelending.com/partners"
                  className={DISCLAIMER_LINK_CLASS_NAME}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              partnersLink2: (
                <a
                  href="https://symplelending.com/partners"
                  className={DISCLAIMER_LINK_CLASS_NAME}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              consentsLink: (
                <a
                  href="https://symplelending.com/partners"
                  className={DISCLAIMER_LINK_CLASS_NAME}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </p>
      </Disclaimer>
      <Button
        id="ssn-verification-submit"
        name="ssn-verification-submit"
        type="submit"
        disabled={loading || !isValidLast4SSN(formData.ssnLast4 || '')}
        loading={loading}
        loadingText={t('ssnVerification.submitting')}
        fullWidth
        size="lg"
      >
        {t('ssnVerification.submit')}
      </Button>
    </form>
  );
}